import { Card, CardContent } from '@/components/ui/card'
import { EstateForMainPageFragment } from '@gql'
import { useHasuraClaim } from '@nhost/react'
import { formatDate } from '@almaris/shared/utils/dates'
import { InfoIcon } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { EstateEstimateForm } from './EstateEstimateForm'
import {
  canEstateBeEstimated,
  EstateEstimateTodoList,
} from './EstateEstimateTodoList'
import { EstateSendMailEstimateButton } from './EstateSendMailEstimateButton'
import useCurrentSellerMember from '@/seller/hooks/useCurrentSeller'
import useAgencyMember from '@/agencyMember/hooks/useAgencyMember'

type EstateEstimatePriceCardProps = {
  estate: Pick<
    EstateForMainPageFragment,
    | 'id'
    | 'type'
    | 'archived'
    | 'name'
    | 'address'
    | 'zipCode'
    | 'city'
    | 'features'
    | 'servicesAndConditions'
    | 'startEstimate'
    | 'endEstimate'
    | 'estimationAskedBy'
    | 'estimationAskedDate'
    | 'estimationPublishedDate'
  >
  refetchEstate: () => void
}
export const EstateEstimatePriceCard = ({
  estate,
  refetchEstate,
}: EstateEstimatePriceCardProps) => {
  const { t } = useTranslation()

  const {
    startEstimate,
    endEstimate,
    estimationAskedBy,
    estimationAskedDate,
    estimationPublishedDate,
  } = estate

  const sellerMember = useCurrentSellerMember()
  const agencyMember = useAgencyMember()

  const canBeEstimated = canEstateBeEstimated(estate)
  const isSellerMember = sellerMember != null
  const isAgencyMember = agencyMember != null
  const isPublished = !!estimationPublishedDate
  const isEstimated = !!startEstimate && !!endEstimate
  const canAskEstimate =
    isSellerMember &&
    !isEstimated &&
    !estimationAskedDate &&
    sellerMember.canEditAndCreateEstate

  const estimationAskedByLabel = useMemo(() => {
    if (estimationAskedBy?.id === sellerMember?.id) return t('common.yourself')
    return estimationAskedBy?.user?.displayName
  }, [estimationAskedBy, sellerMember, t])

  const estimationAskedFormattedDate = useMemo(() => {
    return formatDate(estimationAskedDate)
  }, [estimationAskedDate])

  return (
    <div className="tw-space-y-4">
      <p className="tw-text-2xl tw-font-semibold">
        {t('EstateEstimatePriceCard.heading')}
      </p>
      <Card>
        <CardContent className="tw-grid tw-gap-4 tw-pt-4">
          {!(isSellerMember && isEstimated && isPublished) && (
            <EstateEstimateTodoList
              estate={estate}
              canAskEstimate={canAskEstimate}
            />
          )}
          {!isEstimated && estimationAskedDate && estimationAskedBy && (
            <div className="tw-bg-green-100 tw-text-green-700 tw-rounded-lg tw-px-6 tw-py-4 tw-flex tw-items-center tw-gap-4">
              <div>
                <InfoIcon className="tw-flex-shrink-0 tw-h-[24px] tw-w-[24px]" />
              </div>
              <div className="tw-text-sm">
                {t('EstateEstimatePriceCard.estimationAsked', {
                  estimationAskedByLabel,
                  estimationAskedFormattedDate,
                })}
              </div>
            </div>
          )}
          {isSellerMember && isEstimated && isPublished && (
            <p className="tw-text-sm">
              {t('EstateEstimatePriceCard.estimation', {
                startEstimate: t('common.currency.simple', {
                  val: startEstimate,
                }),
                endEstimate: t('common.currency.simple', {
                  val: endEstimate,
                }),
              })}
            </p>
          )}
          {canAskEstimate && (
            <EstateSendMailEstimateButton
              estateId={estate.id}
              disabled={!canBeEstimated}
              refetchEstate={refetchEstate}
            />
          )}
          {isAgencyMember && canBeEstimated && (
            <EstateEstimateForm estate={estate} />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
